import { type FragmentType, graphql, useFragment } from '@/generated/gql'
import { numberFormat } from '@/helpers/numbers'

import { useCallback, useEffect, useState } from 'react'
import { useCounter } from 'react-use'

const supplierExistingOrderItemsOrderDataFragment = graphql(`
  fragment SupplierUseExistingOrderItems_OrderDataFragment on GmOrder {
    id
    isCateringTeamOrder
    orderItems {
      items {
        isCustom
        id
        name
        note
        addedByUserId
        addedBy {
          id
          fullName
        }
        priceExTax
        price
        qty
        taxRate
        quantityAdjustable
        liveMenuItem {
          _id
          variations(varType: "gm") {
            minQty
          }
        }
        orderItemOptions {
          qty
          id
          name
          priceExTax
          price
          taxRate
          optionReference
          optionType
        }
      }
    }
  }
`)

type CustomerFacingExistingGmOrderItemOptionInternal = Omit<
  CustomerFacingExistingGmOrderItemOption,
  'priceExTax' | 'priceIncTax'
> & {
  priceExTax: number
  priceIncTax: number
}

type CustomerFacingExistingGmOrderItemOption = {
  id: string
  optionCategoryId?: string
  name: string
  priceExTax: string
  priceIncTax: string
  qty: number
  taxRate: number
  optionType: string
}

export type CustomerFacingExistingGmOrderItemInternal = Omit<
  CustomerFacingExistingGmOrderItem,
  'priceExTax' | 'priceIncTax' | 'options'
> & {
  priceExTax: number
  priceIncTax: number
  options: Array<CustomerFacingExistingGmOrderItemOptionInternal>
  minimumQuantity: number
}

export type CustomerFacingExistingGmOrderItem = {
  isCustom: boolean
  isExistingOrderItem: boolean
  hasChanges: boolean
  liveItemId: string
  variationId?: string
  id: string
  name: string
  priceIncTax: string
  priceExTax: string
  taxRate: number
  options: Array<CustomerFacingExistingGmOrderItemOption>
  qty: number
  note?: string
  quantityAdjustable: boolean
  minimumQuantity: number
  addedByUserId?: number | null
  addedByName?: string | null
}

type Props = {
  orderData: FragmentType<typeof supplierExistingOrderItemsOrderDataFragment> | null
  /** The signed-in user; lines they add to a team order are attributed to them. */
  editor?: { id: string; name: string } | null
}

type TemporaryIdInput = {
  itemCoreId: string
  options: Array<{ optionId: string; qty: number }>
}

export const useExistingOrderItems = ({ orderData: orderDataProp, editor }: Props) => {
  const orderData = useFragment(supplierExistingOrderItemsOrderDataFragment, orderDataProp)
  const [orderItemsInternal, setOrderItemsInternal] = useState<
    Array<CustomerFacingExistingGmOrderItemInternal>
  >([])
  const [orderItems, setOrderItems] = useState<Array<CustomerFacingExistingGmOrderItem>>([])
  const [removedOrderItems, setRemovedOrderItems] = useState<
    Array<CustomerFacingExistingGmOrderItemInternal>
  >([])
  const [changesMade, setChangesMade] = useState(false)
  const [newItemCount, newItemCountState] = useCounter(0)

  const itemQuantity = orderItems.reduce((runningTotal, item) => runningTotal + item.qty, 0)

  const createTemporaryId = useCallback((input: TemporaryIdInput) => {
    const options = input.options.map((option) => `${option.optionId}:${option.qty}`).join('-')
    return `temp-${input.itemCoreId}-${options}`
  }, [])

  useEffect(() => {
    if (!orderData) return
    const mappedItemsInternal: Array<CustomerFacingExistingGmOrderItemInternal> =
      orderData.orderItems.items.map((item) => ({
        temporaryId: createTemporaryId({
          itemCoreId: item.liveMenuItem?._id ?? `nolive-${item.id}`,
          options: item.orderItemOptions.map((option) => ({
            qty: option.qty,
            optionId: option.optionReference,
          })),
        }),
        id: item.id,
        hasChanges: false,
        isCustom: item.isCustom,
        quantityAdjustable: item.quantityAdjustable ?? true,
        isExistingOrderItem: true,
        name: item.name,
        note: item.note ?? undefined,
        priceExTax: item.priceExTax,
        priceIncTax: item.price,
        taxRate: item.taxRate,
        qty: item.qty,
        addedByUserId: item.addedByUserId,
        addedByName: item.addedBy?.fullName,
        liveItemId: item.liveMenuItem?._id ?? `nolive-${item.id}`,
        minimumQuantity:
          ((
            item.liveMenuItem as
              { variations?: Array<{ minQty?: number | null } | null> } | null | undefined
          )?.variations?.[0]?.minQty ??
            1) ||
          1,
        options: item.orderItemOptions.map<CustomerFacingExistingGmOrderItemOptionInternal>(
          (option) => ({
            id: option.id,
            priceExTax: option.priceExTax,
            priceIncTax: option.price,
            taxRate: option.taxRate,
            name: option.name,
            qty: option.qty,
            optionType: option.optionType ?? '',
          }),
        ),
      }))
    setOrderItemsInternal(mappedItemsInternal)
  }, [orderData])

  useEffect(() => {
    const mappedExternalItems = orderItemsInternal.map<CustomerFacingExistingGmOrderItem>(
      (item) => ({
        ...item,
        priceExTax: numberFormat(item.priceExTax / 100),
        priceIncTax: numberFormat(item.priceIncTax / 100),
        options: item.options.map<CustomerFacingExistingGmOrderItemOption>((option) => ({
          ...option,
          priceIncTax: numberFormat(option.priceIncTax / 100),
          priceExTax: numberFormat(option.priceExTax / 100),
        })),
      }),
    )
    setOrderItems(mappedExternalItems)
  }, [orderItemsInternal])

  const adjustItemQuantity = useCallback(
    (input: { id: string; newQty: number }) => {
      setChangesMade(true)
      setOrderItemsInternal((prev) => {
        const newOrderItems = [...prev]
        const existingItem = orderData?.orderItems.items.find((item) => item.id === input.id)
        const hasChanges = existingItem?.qty !== input.newQty
        const indexOfItem = newOrderItems.findIndex((item) => item.id === input.id)
        if (indexOfItem >= 0) {
          const currentItem = newOrderItems[indexOfItem]
          const minimumQty = currentItem.minimumQuantity ?? 1
          if (input.newQty < minimumQty) {
            return prev
          }
          newOrderItems[indexOfItem] = {
            ...currentItem,
            qty: input.newQty,
            hasChanges,
            options: currentItem.options.map((option) => ({
              ...option,
              qty: input.newQty,
            })),
          }
        }
        return newOrderItems
      })
    },
    [orderData?.orderItems.items, setChangesMade, setOrderItemsInternal],
  )

  const addNewItemToOrder = useCallback(
    (
      itemToAdd: Omit<
        CustomerFacingExistingGmOrderItemInternal,
        'id' | 'isExistingOrderItem' | 'hasChanges' | 'quantityAdjustable' | 'isCustom'
      >,
    ) => {
      setChangesMade(true)
      const temporaryId = createTemporaryId({
        itemCoreId: itemToAdd.liveItemId,
        options: itemToAdd.options.map((option) => ({ optionId: option.id, qty: option.qty })),
      })

      setOrderItemsInternal((prev) => {
        const index = prev.findIndex((item) => item.id === temporaryId)
        if (index >= 0) {
          const newOrderItems = [...prev]
          const existingOrderItem = orderData?.orderItems.items.find(
            (item) => item.id === temporaryId,
          )
          const nextQty = newOrderItems[index].qty + 1
          newOrderItems[index] = {
            ...newOrderItems[index],
            qty: nextQty,
            hasChanges: existingOrderItem?.qty !== nextQty,
            options: newOrderItems[index].options.map((option) => ({
              ...option,
              qty: nextQty,
            })),
          }
          return newOrderItems
        }

        newItemCountState.inc()
        setTimeout(() => newItemCountState.dec(), 2000)

        // Mirrors what the backend stamps on save, so the line shows "Added by" straight away.
        const attribution =
          orderData?.isCateringTeamOrder && editor
            ? { addedByUserId: Number(editor.id), addedByName: editor.name }
            : {}

        return [
          ...prev,
          {
            id: temporaryId,
            isExistingOrderItem: false,
            hasChanges: true,
            quantityAdjustable: true,
            isCustom: false,
            ...attribution,
            ...itemToAdd,
          },
        ]
      })
    },
    [
      createTemporaryId,
      editor,
      newItemCountState,
      orderData?.isCateringTeamOrder,
      orderData?.orderItems.items,
      setChangesMade,
      setOrderItemsInternal,
    ],
  )

  const removeItemFromOrder = useCallback(
    (input: { id: string }) => {
      setChangesMade(true)
      setOrderItemsInternal((prev) => {
        const newOrderItems = [...prev]
        const indexOfItem = newOrderItems.findIndex((item) => item.id === input.id)
        if (indexOfItem >= 0) {
          const removedItems = newOrderItems.splice(indexOfItem, 1)
          setRemovedOrderItems((prev) => [
            ...prev,
            ...removedItems.filter((item) => item.isExistingOrderItem),
          ])
        }
        return newOrderItems
      })
    },
    [setChangesMade, setOrderItemsInternal, setRemovedOrderItems],
  )

  return {
    orderItems,
    orderItemsInternal,
    removedOrderItems,
    changesMade,
    itemQuantity,
    newItemWasJustAdded: newItemCount > 0,
    adjustItemQuantity,
    removeItemFromOrder,
    addNewItemToOrder,
  }
}

export type UseExistingOrderItemsResult = ReturnType<typeof useExistingOrderItems>
